import { useEffect, useState } from "react";
import { MessageSquarePlus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const ROLE_LABELS = {
  teacher: "Teacher questions",
  technician: "Technician questions",
};

const blankQuestion = () => "";

export function StarterQuestionsSettings() {
  const [questions, setQuestions] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/chat-starter-questions")
      .then((response) => setQuestions(response.data))
      .catch((error) => toast.error(errMsg(error)));
  }, []);

  const updateQuestion = (role, index, value) => {
    setQuestions((current) => ({
      ...current,
      [role]: current[role].map((question, questionIndex) => (
        questionIndex === index ? value : question
      )),
    }));
  };
  const addQuestion = (role) => {
    setQuestions((current) => ({
      ...current,
      [role]: [...current[role], blankQuestion()],
    }));
  };
  const removeQuestion = (role, index) => {
    setQuestions((current) => ({
      ...current,
      [role]: current[role].filter((_, questionIndex) => questionIndex !== index),
    }));
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/chat-starter-questions", questions);
      setQuestions(data);
      toast.success("Ask Jonny questions saved");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  if (!questions) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="starter-questions-loading">
        Loading questions…
      </p>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="starter-questions-settings">
      {Object.entries(ROLE_LABELS).map(([role, label]) => (
        <section key={role} className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold">{label}</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addQuestion(role)}
              disabled={questions[role].length >= 8}
              data-testid={`starter-questions-add-${role}`}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add question
            </Button>
          </div>
          <div className="space-y-2">
            {questions[role].map((question, index) => (
              <div
                key={`${role}-${index}`}
                className="flex items-center gap-2"
                data-testid={`starter-question-row-${role}-${index}`}
              >
                <Input
                  value={question}
                  maxLength={180}
                  onChange={(event) => updateQuestion(role, index, event.target.value)}
                  data-testid={`starter-question-input-${role}-${index}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeQuestion(role, index)}
                  disabled={questions[role].length <= 1}
                  aria-label={`Remove ${label.toLowerCase()} question ${index + 1}`}
                  data-testid={`starter-question-remove-${role}-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </section>
      ))}
      <Button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="starter-questions-save-button"
      >
        <MessageSquarePlus className="mr-2 h-4 w-4" />
        {saving ? "Saving…" : "Save questions"}
      </Button>
    </div>
  );
}